import helper_functions from "../../Config/helper_functions";
import type { AppDispatch } from "../../Redux/store";
import type { RootState } from "../../Redux/store";
import { setTemplateClearPhase2 } from "../../Redux/DownloadSlice/downloadSlice";
import { setTemplateBatch } from "../../Redux/Templates/templateSlice";
import {
  setCreateTemplateTilePattern1Draft,
  setTemplate,
  setTemplate_Edit,
} from "../../Redux/UserSlice/userSlice";
import {
  getTemplateMetadataRootFromGetResponse,
  tileDraftFromTemplateApiRoot,
} from "../../Utils/createTemplateTilePayload";
import {
  setTemplateData,
  setTemplateLoaded,
} from "../../Redux/WaterMarks/watermarks";
import {
  sendDeleteCallToSever,
  sendGetCallToSever,
  sendPatchCallToSever,
  sendPostCallToSever,
} from "../Axios/Axios";
import UploadMedia from "../UploadMedia/uploadMedia";
import { freeTierSlot } from "../../Utils/freeTierWatermarks";
import { setLoader } from "../../Redux/MainImage/imageSlice";
import { setWaterMarksLoader } from "../../Redux/WaterMarks/watermarks";

type BatchTemplateIDs = { landcsapeTemp?: string; portraitTemp?: string };

interface UserSliceState {
  edit_Template: boolean;
  create_Template: boolean;
  current_TemplateID: string | BatchTemplateIDs | null;
}

function _getTemplateId(id: string | BatchTemplateIDs | null): string | null {
  if (id == null) return null;
  if (typeof id === "string") return id;
  return id.landcsapeTemp || id.portraitTemp || null;
}

interface SourceItem {
  local_id: string;
  src: string | Blob;
}

const SaveTemplate =
  (
    data: { watermarks?: Array<Record<string, unknown>> },
    tempLarge: string,
    tempThumb: string,
    sources: SourceItem[],
    userSliceState: UserSliceState
  ) => async (dispatch: AppDispatch) => {
    const templateIdStr =
      typeof userSliceState.current_TemplateID === "string"
        ? userSliceState.current_TemplateID
        : userSliceState.current_TemplateID && typeof userSliceState.current_TemplateID === "object"
        ? (userSliceState.current_TemplateID.landcsapeTemp ||
            userSliceState.current_TemplateID.portraitTemp ||
            "")
        : "";
    const url =
      userSliceState.edit_Template && !userSliceState.create_Template && templateIdStr
        ? `templates/${templateIdStr}`
        : "templates";

    if (userSliceState.edit_Template && userSliceState.create_Template) {
      // Iterate through each watermark object and remove the 'serverId' property
      data.watermarks?.forEach((watermark) => delete watermark.serverId);
    }
    // SEND CALL TO SERVER TO LOGIN FROM PUNCH
    try {
      const resp = await sendPostCallToSever(
        data,
        url,
        userSliceState.edit_Template && !userSliceState.create_Template
      );
      const apiData = resp?.data as { id?: string; urls?: unknown[] } | undefined;
      if (!apiData?.id) {
        console.error("Template save failed: missing template id in API response", resp);
        return false;
      }
      const result = (apiData.urls ?? []) as Array<{
        local_id?: string;
        main_pre_signed_url?: string;
        thumb_pre_signed_url?: string;
      }>;
      const promises: Promise<unknown>[] = [];
      dispatch(setTemplateClearPhase2(false));
      // Match admin: use for-of to properly collect all upload promises (map+async can race)
      for (const item of result) {
        if (item.local_id === "template") {
          const buffer1 = helper_functions.dataURLtoFile(
            tempLarge,
            "template_main"
          );
          promises.push(UploadMedia(item.main_pre_signed_url, buffer1));
          const buffer2 = helper_functions.dataURLtoFile(
            tempThumb,
            "template_main"
          );
          promises.push(UploadMedia(item.thumb_pre_signed_url, buffer2));
        } else if (item.local_id) {
          const srcItem = sources.filter((seq) => item.local_id === seq.local_id);
          if (srcItem.length && srcItem[0]?.src != null) {
            const fileSrc = srcItem[0].src;
            if (typeof fileSrc === "string") {
              // Match admin: data URLs use dataURLtoFile; http(s) URLs use WebToFile
              if (fileSrc.startsWith("http")) {
                promises.push(
                  helper_functions
                    .WebToFile(fileSrc)
                    .then((dataFile) =>
                      UploadMedia(item.main_pre_signed_url, dataFile)
                    )
                );
              } else {
                const dataFile = helper_functions.dataURLtoFile(
                  fileSrc,
                  "main_pre_signed_url"
                );
                if (dataFile) {
                  promises.push(
                    UploadMedia(item.main_pre_signed_url, dataFile)
                  );
                }
              }
            } else {
              const dataFile = helper_functions.blobToFile(
                fileSrc,
                "main_pre_signed_url"
              );
              promises.push(UploadMedia(item.main_pre_signed_url, dataFile));
            }
          }
        }
      }
      await Promise.all(promises);
      return dispatch(VerifyTemplate(apiData.id));
    } catch (error) {
      console.log("Save template error: ", error);
      return false;
    }
  };

const VerifyTemplate = (id: string) => (dispatch: AppDispatch) => {
  if (!id || typeof id !== "string") {
    return Promise.resolve(false);
  }
  return sendPatchCallToSever(
    { status: "Completed" },
    `templates/status/${id}`
  )
    .then((response) => {
      const status = (response as { status?: number })?.status;
      if (status != null && status >= 200 && status < 300) {
        dispatch(GetAllTemplates());
        return true;
      }
      return false;
    })
    .catch(() => false);
};
const GetAllTemplates = () => (dispatch: AppDispatch) => {
  return sendGetCallToSever(`templates/getAll`)
    .then((response) => {
      if (response.hasOwnProperty("data")) dispatch(setTemplate(response.data.templates));
      dispatch(setLoader(false));
    })
    .catch((error) => {
      console.log("Get all templates Error", error);
      // Without this, a failed fetch left `loader` stuck true forever for any caller that
      // turns it on beforehand expecting this action to turn it back off on both success and
      // failure (e.g. AddTemplate.tsx's "My Templates" page).
      dispatch(setLoader(false));
    });
};
// Free tier: only one watermark per slot (one image/sticker/emoji/qrcode, one text/date, one
// signature). Keeps the first of each slot found and drops the rest.
//
// The slotting itself now lives in Utils/freeTierWatermarks so that the add-time guards enforce
// the EXACT same rule this load-time filter does -- they disagreed before (add-time keyed off the
// raw `property`, so sticker and image looked like different types), which let a free user build
// a template this function would then silently strip a watermark out of.
function filterWatermarksForFreeUser(
  items: Record<string, unknown>[]
): Record<string, unknown>[] {
  const seenSlots = new Set<string>();
  return items.filter((item) => {
    const slot = freeTierSlot(String(item.property ?? item.type ?? ""));
    if (seenSlots.has(slot)) return false;
    seenSlots.add(slot);
    return true;
  });
}

// Fetches one template from the server, decrypts each watermark, and stores the result in
// Redux (both the template cache and, if `setItems`, the live canvas items).
const GetTemplateData =
  (
    id: string,
    prevState: Record<string, unknown>[] | null | undefined,
    setEdit: boolean | undefined,
    setItems: boolean | undefined,
    canvasWidth?: number,
    isPremium?: boolean
  ) => (dispatch: AppDispatch, getState: () => RootState) => {
    return sendGetCallToSever(`templates/${id}`)
      .then(async (response) => {
        dispatch(setWaterMarksLoader(true));

        try {
          if (response && Object.prototype.hasOwnProperty.call(response, "data")) {
            // Support both shapes:
            // - { data: { watermarks: [] } }
            // - { data: { data: { watermarks: [] } } }
            const items = Array.isArray(response?.data?.watermarks)
              ? response.data.watermarks
              : Array.isArray(response?.data?.data?.watermarks)
                ? response.data.data.watermarks
                : [];

            const platform =
              response?.data?.platform || response?.data?.data?.platform;

            const templateMetaRoot =
              getTemplateMetadataRootFromGetResponse(response);
            const tileDraft = tileDraftFromTemplateApiRoot(templateMetaRoot);
            dispatch(
              setCreateTemplateTilePattern1Draft(
                tileDraft
              )
            );
            const tileBatchFields = {
              patternType: templateMetaRoot.patternType,
              tileData: templateMetaRoot.tileData,
            };

            const baseArray = [...(prevState || [])];

            // Get canvas width from state if not provided as parameter
            const state = getState();
            const effectiveCanvasWidth =
              canvasWidth ?? state.userSlice.canvasSize?.width ?? 651;

            if (!items.length) {
              dispatch(
                setTemplateBatch({
                  id: id,
                  watermarks: baseArray,
                  platform: platform ?? "",
                  ...tileBatchFields,
                } as any)
              );
              if (setItems) {
                dispatch(setTemplateData(baseArray as any));
                dispatch(setTemplateLoaded(true));
              } else {
                dispatch(
                  setTemplateBatch({
                    id: id,
                    watermarks: baseArray,
                    platform: platform ?? "",
                    ...tileBatchFields,
                  } as any)
                );
              }
              if (setEdit) dispatch(setTemplate_Edit(true));
              dispatch(setLoader(false));
              dispatch(setWaterMarksLoader(false));
              return { id, watermarks: baseArray, tileDraft: tileDraftFromTemplateApiRoot(templateMetaRoot) };
            }

            const processed = await Promise.all(
              items.map(async (item) => {
                let raw: Record<string, unknown> = {};
                try {
                  raw =
                    typeof item?.data === "string"
                      ? JSON.parse(item.data)
                      : (item?.data as Record<string, unknown>) || {};
                } catch {
                  raw = {};
                }

                const tag =
                  typeof raw?.tag === "number"
                    ? (raw.tag as number)
                    : Number.isFinite(Number(raw?.tag))
                      ? Number(raw.tag)
                      : null;

                if (raw.type !== "text" && item?.media?.url) {
                  try {
                    const file = await helper_functions.WebToFile(item.media.url);
                    raw = { ...raw, src: file };
                  } catch (error) {
                    console.error("Error loading image:", error);
                  }
                  // Match admin: keep mediaUrl so we can re-fetch when saving (File may be lost)
                  raw = { ...raw, mediaUrl: item.media.url };
                }

                // IMPORTANT: match admin semantics:
                // - editor watermark `id` should be the stable `localId`
                // - `serverId` is used for patching existing templates
                // Use item.serverId when backend provides it; otherwise raw?.id (watermark's own id from data).
                // Do NOT fall back to item?.id - it can be shared (e.g. template-level) and cause duplicate serverIds.
                const serverId = (item?.serverId ?? raw?.id) as string | undefined;
                const localId = (item?.localId ?? raw?.id ?? serverId) as string;
                raw = { ...raw, id: localId, localId, serverId };

                const decrypted = await helper_functions.dataWrapperDecrypt(
                  raw,
                  platform,
                  effectiveCanvasWidth
                );

                if (tileDraft.tiledEnabled) {
                  decrypted.deg = 0;
                  decrypted.opacity = 1;
                }

                return { wm: decrypted, tag };
              })
            );

            // Enforce: index 0 = lowest z-index, index 1 above, ...
            // We treat `tag` as the persisted order (set during save).
            const sorted = [...processed].sort((a, b) => {
              const at = a.tag;
              const bt = b.tag;
              if (at === null && bt === null) return 0;
              if (at === null) return 1;
              if (bt === null) return -1;
              return at - bt;
            });

            const rawWatermarks = [...baseArray, ...sorted.map((x) => x.wm)];
            const finalWatermarks =
              isPremium === false
                ? filterWatermarksForFreeUser(rawWatermarks as Record<string, unknown>[])
                : rawWatermarks;
            // Always refresh cache by template id so subsequent apply paths
            // (which use GetTemplateDataFromRedux) do not use stale watermark data.
            dispatch(
              setTemplateBatch({
                id: id,
                watermarks: finalWatermarks,
                platform: platform ?? "",
                ...tileBatchFields,
              } as any)
            );

            if (setItems) {
              dispatch(setTemplateData(finalWatermarks as any));
              dispatch(setTemplateLoaded(true));
            } else {
              dispatch(
                setTemplateBatch({
                  id: id,
                  watermarks: finalWatermarks,
                  platform: platform ?? "",
                  ...tileBatchFields,
                } as any)
              );
            }
             if (setEdit) dispatch(setTemplate_Edit(true));

            dispatch(setLoader(false));
            dispatch(setWaterMarksLoader(false));
            return { id, watermarks: finalWatermarks, tileDraft: tileDraftFromTemplateApiRoot(templateMetaRoot) };
          }

          dispatch(setLoader(false));
          dispatch(setWaterMarksLoader(false));
          return false;
        } catch (error) {
          console.log("Get template data Error", error);
          dispatch(setLoader(false));
          dispatch(setWaterMarksLoader(false));
          return false;
        }
      })
      .catch((error) => {
        console.log("Get template data Error", error);
        dispatch(setLoader(false));
        dispatch(setWaterMarksLoader(false));
        return false;
      });
  };

interface TemplateWatermark {
  data?: string;
  media?: { url?: string };
  localId?: string;
  serverId?: string;
  id?: unknown;
  [key: string]: unknown;
}

interface TemplateItem {
  id: string;
  watermarks: TemplateWatermark[];
  platform?: string;
  patternType?: unknown;
  tileData?: unknown;
  [key: string]: unknown;
}

// Same result as `GetTemplateData`, but reads the template from Redux's own cache instead of
// calling the server again -- used when a template was already fetched once this session
// (e.g. switching between batch photos that share the same template).
const GetTemplateDataFromRedux =
  (
    id: string,
    templates: { templates: TemplateItem[] },
    _waterMarksState: unknown,
    edit: boolean
  ) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setWaterMarksLoader(true));
    if (edit) dispatch(setTemplate_Edit(true));

    const template = templates.templates.find((item) => item.id === id);

    if (!template) {
      console.error("Template not found in Redux!");
      dispatch(setLoader(false));
      dispatch(setWaterMarksLoader(false));
      return false;
    }

    const tileDraft = tileDraftFromTemplateApiRoot(
      template as Record<string, unknown>
    );
    dispatch(
      setCreateTemplateTilePattern1Draft(
        tileDraft
      )
    );

    const { watermarks, platform } = template;
    const firstWatermark = watermarks[0];
    
    // Redux may already hold decrypted watermarks (e.g. from an earlier `GetTemplateData` call)
    // instead of the raw `data` JSON string straight from the server -- skip decrypting again if so.
    const isAlreadyDecrypted =
      firstWatermark?.src !== undefined || 
      (firstWatermark?.type !== undefined && !firstWatermark?.data);

    // Enforce persisted order (tag 0 lowest). If tag missing, keep current order.
    const orderedWatermarks = [...(watermarks || [])].sort((a: TemplateWatermark, b: TemplateWatermark) => {
      const readTag = (wm: TemplateWatermark) => {
        try {
          if (typeof wm?.data === "string") {
            const parsed = JSON.parse(wm.data);
            const t = parsed?.tag;
            if (typeof t === "number") return t;
            const n = Number(t);
            return Number.isFinite(n) ? n : null;
          }
          return null;
        } catch {
          return null;
        }
      };
      const at = readTag(a);
      const bt = readTag(b);
      if (at === null && bt === null) return 0;
      if (at === null) return 1;
      if (bt === null) return -1;
      return at - bt;
    });

    let finalWatermarks = orderedWatermarks;

    if (!isAlreadyDecrypted) {
      // Get canvas width from userState instead of waterMarksState
      const state = getState();
      const canvasWidth = state.userSlice.canvasSize?.width ?? 651;

      finalWatermarks = await Promise.all(
        orderedWatermarks.map(async (wm) => {
          const data: Record<string, unknown> = JSON.parse(wm.data || "{}");

          // Fetch image for non-text watermarks
          if (data.type !== "text" && wm.media?.url) {
            try {
              const file = await helper_functions.WebToFile(wm.media.url);
              data.src = file;
            } catch (error) {
              console.error("Error loading image:", error);
            }
            // Match admin: keep mediaUrl for re-fetch when saving
            data.mediaUrl = wm.media.url;
          }

          // Match admin semantics:
          data.id = wm.localId || wm.serverId;
          data.localId = wm.localId || wm.serverId;
          data.serverId = wm.serverId ?? undefined;

          // Decrypt watermark data
          const decrypted = await helper_functions.dataWrapperDecrypt(
            data,
            platform ?? "",
            canvasWidth
          );
          if (tileDraft.tiledEnabled) {
            decrypted.deg = 0;
            decrypted.opacity = 1;
          }
          return decrypted as TemplateWatermark;
        })
      ) as TemplateWatermark[];
    } else {
      // Already decrypted: still normalize ids to localId semantics.
      finalWatermarks = (orderedWatermarks || []).map((wm) => {
        const localId = (wm?.localId || wm?.id || wm?.serverId) as string;
        const serverId = wm?.serverId ?? undefined;
        const next = { ...wm, id: localId, localId, serverId } as TemplateWatermark;
        if (tileDraft.tiledEnabled) {
          next.deg = 0;
          next.opacity = 1;
        }
        if (next?.type === "text" && typeof next?.font !== "undefined") {
          next.font = helper_functions.normalizeTemplateFontForRender(next.font);
        }
        return next;
      });
    }

    dispatch(setTemplateData(finalWatermarks as any));
    dispatch(setTemplateLoaded(true));
    dispatch(setLoader(false));
    dispatch(setWaterMarksLoader(false));
    
    return true;
  };

const DeleteTemplateByID = (id: string) => (dispatch: AppDispatch) => {
  return sendDeleteCallToSever(`templates/${id}/web`).then(async () => {
    dispatch(GetAllTemplates());
  });
};

const DeleteAllTemplates = () => (dispatch: AppDispatch) => {
  return sendDeleteCallToSever(`templates/all`).then(async () => {
    dispatch(GetAllTemplates());
  });
};

const RenameTemplate = (id: string, name: string) => async (dispatch: AppDispatch) => {
  if (!id || !name.trim()) return false;
  try {
    // Dedicated rename endpoint. The previous call reused the general template-update route
    // (`PATCH templates/:id`) with a name-only body plus `deletedIds: []`, which the backend
    // answered with a 500 -- that route expects a full template payload, so a partial one blew
    // up server-side. This endpoint takes just the name.
    const response = await sendPatchCallToSever(
      {
        name: name.trim(),
      },
      `templates/rename/${id}`
    );
    const status = (response as { status?: number })?.status;
    if (status != null && status >= 200 && status < 300) {
      await dispatch(GetAllTemplates());
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error renaming template:", error);
    return false;
  }
};

export {
  SaveTemplate as CreateTemplate,
  VerifyTemplate,
  GetAllTemplates,
  GetTemplateData,
  GetTemplateDataFromRedux,
  DeleteTemplateByID,
  DeleteAllTemplates,
  RenameTemplate,
};
